import { forwardRef } from 'react';
import { MenuItem, type MenuItemProps } from '@mui/material';

export type MenuLinkProps = Omit<MenuItemProps<'a'>, 'component'> & {
  /** Navigation target; rendered on a real `<a>`. */
  href: string;
};

/**
 * MenuLink - a navigation row inside a `Menu`.
 *
 * A MUI `MenuItem` rendered as an anchor and styled as a text link (underline
 * on hover, no row height, no hover fill) rather than a menu row. Use it instead
 * of nesting a `Link` in a `Box`: `MenuList` moves arrow-key focus only across
 * its direct items and closes on Tab, so a nested link is unreachable by
 * keyboard. As a real anchor it also keeps native open-in-new-tab semantics.
 *
 * @example
 * <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
 *   <MenuLink href="/auth/change-password">Change password</MenuLink>
 *   <MenuItem onClick={logout}>Log out</MenuItem>
 * </Menu>
 */
export const MenuLink = forwardRef<HTMLAnchorElement, MenuLinkProps>(({ sx, ...props }, ref) => (
  <MenuItem
    ref={ref}
    component="a"
    sx={[
      {
        color: 'common.blue',
        minHeight: 'auto',
        '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
    {...props}
  />
));

MenuLink.displayName = 'MenuLink';
